import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, ChevronRight, User, Crown, Users, Building2, FileText, MessageSquare, MessageCircle, Bell, BookOpen, Award, Briefcase, Heart, GraduationCap, FolderTree, Sparkles, Percent, HelpCircle, Inbox } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useNotificationStore } from '../stores/notificationStore';
import { useMessageStore } from '../stores/messageStore';

export default function AdminSidebar() {
  const { user } = useAuthStore();
  const { unreadCount, fetchUnreadCount } = useNotificationStore();
  const dmUnread = useMessageStore((s) => s.unreadCount);
  const fetchDmUnread = useMessageStore((s) => s.fetchUnreadCount);
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['my-info', 'provider-mode', 'employer-mode', 'admin-mode']);
  const [premiumInquiryUnread, setPremiumInquiryUnread] = useState(0);

  const isAdmin = user?.role === 'admin';
  // 교육 기관(provider) 또는 관리자: 수업/인증서 메뉴
  const isProviderOrAdmin = user?.role === 'provider' || user?.role === 'admin';
  // 기업 회원(employer) 또는 관리자: 채용 메뉴
  const isEmployerOrAdmin = user?.role === 'employer' || user?.role === 'admin';
  // 일반 회원 전용 기능 (수강 신청·인증서 보관함·이력서) — admin은 검토용으로 노출
  const showLearnerMenus = !user?.role || user.role === 'user' || user.role === 'admin';
  const isPremium = user?.is_premium === true;
  const isOnProfilePage = location.pathname === '/profile';
  const isOnEnrollmentsPage = location.pathname === '/profile/enrollments';
  const isOnFavoritesPage = location.pathname === '/profile/favorites';
  const isOnApplicationsPage = location.pathname === '/profile/applications';
  const isOnCertificatesPage = location.pathname === '/profile/certificates';
  const isOnResumePage = location.pathname === '/profile/resume';
  const isOnMessagesPage = location.pathname === '/messages';
  const isOnChatPage = location.pathname.startsWith('/chat');
  const isOnProviderPage = location.pathname.startsWith('/provider');
  const isOnEmployerPage = location.pathname.startsWith('/employer');
  const isOnProviderPremiumPage = location.pathname === '/provider/premium';
  const isOnProviderPostsPage = location.pathname === '/provider/posts';
  const isOnEmployerRecruitmentPage = location.pathname === '/employer/recruitment';
  const isOnAdminEmployersPage = location.pathname === '/admin/employers';
  const isOnAdminPage = location.pathname.startsWith('/admin');
  const isOnAdminUsersPage = location.pathname === '/admin' || location.pathname === '/admin/users';
  const isOnAdminPremiumPage = location.pathname === '/admin/premium';
  const isOnAdminProviderApprovalsPage = location.pathname === '/admin/provider-approvals';
  const isOnAdminProviderCommissionPage = location.pathname === '/admin/provider-commission';
  const isOnAdminDegreeReviewPage = location.pathname === '/admin/degree-review';
  const isOnAdminCertificateReviewPage = location.pathname === '/admin/certificate-review';
  const isOnAdminEducationCategoriesPage = location.pathname === '/admin/education-categories';
  const isOnAdminBadgeTreePage = location.pathname === '/admin/badge-tree';
  const isOnAdminPremiumIntroPage = location.pathname === '/admin/premium-intro';
  const isOnAdminPremiumInquiriesPage = location.pathname === '/admin/premium-inquiries';
  const isOnMyInfoPage = location.pathname.startsWith('/profile');

  // 읽지 않은 알림 + DM 수 주기적으로 갱신
  useEffect(() => {
    fetchUnreadCount();
    fetchDmUnread();
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchDmUnread();
    }, 30000); // 30초마다
    return () => clearInterval(interval);
  }, [fetchUnreadCount, fetchDmUnread]);

  // 우수 인증 신청 미확인 건수 (admin 전용) 주기적으로 갱신
  useEffect(() => {
    if (!isAdmin) return;
    const fetchPremiumInquiryUnread = async () => {
      try {
        const res = await fetch('/api/v1/admin/premium-inquiries/unread-count', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setPremiumInquiryUnread(data.count || 0);
        }
      } catch {
        /* noop */
      }
    };
    fetchPremiumInquiryUnread();
    const interval = setInterval(fetchPremiumInquiryUnread, 30000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // 현재 경로에 따라 메뉴 자동 펼치기
  useEffect(() => {
    const menusToExpand: string[] = [];

    if (isOnMyInfoPage) {
      menusToExpand.push('my-info');
    }

    if (isOnProviderPage) {
      menusToExpand.push('provider-mode');
    }

    if (isOnEmployerPage) {
      menusToExpand.push('employer-mode');
    }

    if (isOnAdminPage) {
      menusToExpand.push('admin-mode');
    }

    if (menusToExpand.length > 0) {
      setExpandedMenus(prev => {
        const newMenus = [...prev];
        menusToExpand.forEach(menu => {
          if (!newMenus.includes(menu)) {
            newMenus.push(menu);
          }
        });
        return newMenus;
      });
    }
  }, [isOnMyInfoPage, isOnProviderPage, isOnEmployerPage, isOnAdminPage]);

  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev =>
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  return (
    <aside className="w-80 min-w-80 ml-6 sm:ml-10 lg:ml-16 bg-white border-r border-gray-200 overflow-y-auto shrink-0">
      <div className="px-6 py-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">메뉴</h2>
        <nav className="space-y-1">
          {/* 내 정보 */}
          <div>
            <button
              onClick={() => toggleMenu('my-info')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                isOnMyInfoPage
                  ? 'bg-primary/5 text-primary'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-3">
                <User className="w-5 h-5" />
                <span className="font-medium">내 정보</span>
              </div>
              <ChevronRight
                className={`w-4 h-4 transition-transform ${
                  expandedMenus.includes('my-info') ? 'rotate-90' : ''
                }`}
              />
            </button>
            {/* 하위 메뉴 */}
            {expandedMenus.includes('my-info') && (
              <div className="ml-8 mt-1 space-y-1">
                <button
                  onClick={() => navigate('/profile')}
                  className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    isOnProfilePage
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <User className="w-4 h-4" />
                  회원 정보 변경
                </button>
                {/* 일반 회원 전용 (provider/employer는 숨김, admin은 노출) */}
                {showLearnerMenus && (
                  <>
                    <button
                      onClick={() => navigate('/profile/enrollments')}
                      className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        isOnEnrollmentsPage
                          ? 'bg-primary text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <BookOpen className="w-4 h-4" />
                      내 수강 신청 관리
                    </button>
                    <button
                      onClick={() => navigate('/profile/favorites')}
                      className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        isOnFavoritesPage
                          ? 'bg-primary text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Heart className="w-4 h-4" />
                      관심 수업
                    </button>
                    <button
                      onClick={() => navigate('/profile/applications')}
                      className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        isOnApplicationsPage
                          ? 'bg-primary text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Briefcase className="w-4 h-4" />
                      지원 목록
                    </button>
                    <button
                      onClick={() => navigate('/profile/certificates')}
                      className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        isOnCertificatesPage
                          ? 'bg-primary text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Award className="w-4 h-4" />
                      인증서 보관함
                    </button>
                    <button
                      onClick={() => navigate('/profile/resume')}
                      className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        isOnResumePage
                          ? 'bg-primary text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <FileText className="w-4 h-4" />
                      이력서
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* 알림 (시스템 알림함) */}
          <button
            onClick={() => navigate('/messages')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
              isOnMessagesPage
                ? 'bg-primary/10 text-primary'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Bell className="w-5 h-5" />
            <span className="font-medium">알림</span>
            {unreadCount > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[1.25rem] text-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {/* 메시지 (1:1 DM 채팅) */}
          <button
            onClick={() => navigate('/chat')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
              isOnChatPage
                ? 'bg-primary/10 text-primary'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <MessageCircle className="w-5 h-5" />
            <span className="font-medium">메시지</span>
            {dmUnread > 0 && (
              <span className="ml-auto bg-accent text-white text-xs rounded-full px-2 py-0.5 min-w-[1.25rem] text-center">
                {dmUnread > 99 ? '99+' : dmUnread}
              </span>
            )}
          </button>

          {/* 교육 기관 (provider/admin) — 수업/인증서 */}
          {isProviderOrAdmin && (
            <div>
              <button
                onClick={() => toggleMenu('provider-mode')}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                  isOnProviderPage
                    ? 'bg-primary/5 text-primary'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5" />
                  <span className="font-medium">교육 기관</span>
                </div>
                <ChevronRight
                  className={`w-4 h-4 transition-transform ${
                    expandedMenus.includes('provider-mode') ? 'rotate-90' : ''
                  }`}
                />
              </button>
              {/* 하위 메뉴 */}
              {expandedMenus.includes('provider-mode') && (
                <div className="ml-8 mt-1 space-y-1">
                  {isPremium && (
                    <button
                      onClick={() => navigate('/provider/premium')}
                      className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        isOnProviderPremiumPage
                          ? 'bg-primary text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Crown className="w-4 h-4" />
                      우수 인증 교육 기관
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/provider/posts')}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      isOnProviderPostsPage
                        ? 'bg-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    수업 목록
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 기업 회원 (employer/admin) — 채용 */}
          {isEmployerOrAdmin && (
            <div>
              <button
                onClick={() => toggleMenu('employer-mode')}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                  isOnEmployerPage
                    ? 'bg-primary/5 text-primary'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Briefcase className="w-5 h-5" />
                  <span className="font-medium">기업 회원</span>
                </div>
                <ChevronRight
                  className={`w-4 h-4 transition-transform ${
                    expandedMenus.includes('employer-mode') ? 'rotate-90' : ''
                  }`}
                />
              </button>
              {expandedMenus.includes('employer-mode') && (
                <div className="ml-8 mt-1 space-y-1">
                  <button
                    onClick={() => navigate('/employer/recruitment')}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      isOnEmployerRecruitmentPage
                        ? 'bg-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Briefcase className="w-4 h-4" />
                    채용
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 관리자 모드 (관리자만 표시) */}
          {isAdmin && (
            <div>
              <button
                onClick={() => toggleMenu('admin-mode')}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                  isOnAdminPage
                    ? 'bg-primary/5 text-primary'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5" />
                  <span className="font-medium">관리자 모드</span>
                </div>
                <ChevronRight
                  className={`w-4 h-4 transition-transform ${
                    expandedMenus.includes('admin-mode') ? 'rotate-90' : ''
                  }`}
                />
              </button>
              {/* 하위 메뉴 */}
              {expandedMenus.includes('admin-mode') && (
                <div className="ml-8 mt-1 space-y-1">
                  <button
                    onClick={() => navigate('/admin')}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      isOnAdminUsersPage
                        ? 'bg-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Users className="w-4 h-4" />
                    회원 정보 관리
                  </button>
                  <button
                    onClick={() => navigate('/admin/premium')}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      isOnAdminPremiumPage
                        ? 'bg-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Crown className="w-4 h-4" />
                    우수 인증 교육 기관 관리
                  </button>
                  <button
                    onClick={() => navigate('/admin/premium-intro')}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      isOnAdminPremiumIntroPage
                        ? 'bg-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <HelpCircle className="w-4 h-4" />
                    우수 인증 교육 기관 소개
                  </button>
                  <button
                    onClick={() => navigate('/admin/premium-inquiries')}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      isOnAdminPremiumInquiriesPage
                        ? 'bg-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Inbox className="w-4 h-4" />
                    우수 인증 신청 현황
                    {premiumInquiryUnread > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[1.25rem] text-center">
                        {premiumInquiryUnread > 99 ? '99+' : premiumInquiryUnread}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => navigate('/admin/employers')}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      isOnAdminEmployersPage
                        ? 'bg-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Briefcase className="w-4 h-4" />
                    구인 기관 관리
                  </button>
                  <button
                    onClick={() => navigate('/admin/provider-approvals')}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      isOnAdminProviderApprovalsPage
                        ? 'bg-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    교육 기관 검토
                  </button>
                  <button
                    onClick={() => navigate('/admin/provider-commission')}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      isOnAdminProviderCommissionPage
                        ? 'bg-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Percent className="w-4 h-4" />
                    중개수수료율 설정
                  </button>
                  <button
                    onClick={() => navigate('/admin/degree-review')}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      isOnAdminDegreeReviewPage
                        ? 'bg-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <GraduationCap className="w-4 h-4" />
                    학력 검토
                  </button>
                  <button
                    onClick={() => navigate('/admin/certificate-review')}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      isOnAdminCertificateReviewPage
                        ? 'bg-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Award className="w-4 h-4" />
                    자격증·수료증 검토
                  </button>
                  <button
                    onClick={() => navigate('/admin/education-categories')}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      isOnAdminEducationCategoriesPage
                        ? 'bg-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <FolderTree className="w-4 h-4" />
                    교육 카테고리 분류
                  </button>
                  <button
                    onClick={() => navigate('/admin/badge-tree')}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      isOnAdminBadgeTreePage
                        ? 'bg-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Sparkles className="w-4 h-4" />
                    뱃지 트리 테스트
                  </button>
                </div>
              )}
            </div>
          )}
        </nav>
      </div>
    </aside>
  );
}
